import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Heart } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";

const navLinks = [
  { to: "/rooms", label: "Villa", end: false },
  { to: "/shop", label: "Shop All", end: true },
  { to: "/arrivals", label: "Arrivals" },
  { to: "/favourites", label: "Favourites" },
  { to: "/shop/lingerie", label: "Lingerie" },
  { to: "/shop/toys", label: "Pleasure" },
  { to: "/shop/accessories", label: "Accessories" },
  { to: "/shop/basement", label: "The Basement" },
  { to: "/shop/rooftop", label: "The Rooftop" },
  { to: "/shop/wellness", label: "Wellness" },
  { to: "/shop/games", label: "Games" },
  { to: "/gift", label: "Gift" },
  { to: "/shop/companions", label: "Companions" },
  { to: "/journal", label: "Journal" },
];

// Renders a left-fixed vertical rail on lg+ and a compact top bar on mobile.
// This ends the "Journal collides with Admin" collision permanently — Admin
// lives in its own footer row on the rail with a hairline divider above it.
const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { count: wishCount } = useWishlist() || {};
  const [mobileOpen, setMobileOpen] = useState(false);

  const utilityIcons = (
    <>
      <Link
        data-testid="header-wishlist-btn"
        to="/wishlist"
        className="relative text-muted-bone hover:text-bone transition"
        aria-label="Saved"
      >
        <Heart strokeWidth={1.5} size={20} />
        {wishCount > 0 && (
          <span
            data-testid="header-wishlist-count"
            className="absolute -top-2 -right-3 text-[10px] tracking-wider px-1.5 py-0.5 text-bone"
            style={{ background: "var(--rs-oxblood)" }}
          >
            {wishCount}
          </span>
        )}
      </Link>
      <Link
        data-testid="header-account-btn"
        to={user ? "/account" : "/auth"}
        className="text-muted-bone hover:text-bone transition"
        aria-label="Account"
      >
        <User strokeWidth={1.5} size={20} />
      </Link>
      <button
        data-testid="header-cart-btn"
        onClick={() => setOpen(true)}
        className="relative text-muted-bone hover:text-bone transition"
        aria-label="Cart"
      >
        <ShoppingBag strokeWidth={1.5} size={20} />
        {count > 0 && (
          <span
            data-testid="header-cart-count"
            className="absolute -top-2 -right-3 text-[10px] tracking-wider px-1.5 py-0.5 text-bone"
            style={{ background: "var(--rs-oxblood)" }}
          >
            {count}
          </span>
        )}
      </button>
    </>
  );

  return (
    <>
      {/* ==================== MOBILE / TABLET TOP BAR (< lg) ==================== */}
      <header
        data-testid="site-header"
        className="lg:hidden sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ background: "rgba(10,10,10,0.72)", borderColor: "var(--rs-border)" }}
      >
        <div className="px-6 py-5 flex items-center justify-between">
          <button
            data-testid="mobile-nav-toggle"
            className="text-bone"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X strokeWidth={1.5} size={22} /> : <Menu strokeWidth={1.5} size={22} />}
          </button>
          <Link
            data-testid="brand-logo"
            to="/rooms"
            className="font-serif-display text-2xl tracking-tight text-bone"
            style={{ letterSpacing: "0.04em" }}
          >
            Ram Shack
          </Link>
          <div className="flex items-center gap-5">{utilityIcons}</div>
        </div>
        {mobileOpen && (
          <div
            className="border-t px-6 py-6 flex flex-col gap-5"
            style={{ borderColor: "var(--rs-border)", background: "var(--rs-bg)" }}
          >
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                to={l.to}
                end={l.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] ${isActive ? "text-bone" : "text-muted-bone"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user?.is_admin && (
              <NavLink
                data-testid="mobile-nav-link-admin"
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className="text-sm uppercase tracking-[0.22em] pt-4 border-t"
                style={{ color: "var(--rs-terracotta)", borderColor: "var(--rs-border)" }}
              >
                Admin
              </NavLink>
            )}
          </div>
        )}
      </header>

      {/* ==================== DESKTOP LEFT RAIL (lg+) ==================== */}
      <aside
        data-testid="site-sidebar"
        className="hidden lg:flex fixed left-0 top-0 h-screen w-52 z-40 flex-col border-r"
        style={{
          background: "rgba(10,7,10,0.94)",
          borderColor: "var(--rs-border)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Brand */}
        <div className="px-6 pt-8 pb-6">
          <Link
            data-testid="brand-logo"
            to="/rooms"
            className="font-serif-display text-2xl text-bone block leading-tight"
            style={{ letterSpacing: "0.04em" }}
          >
            Ram Shack
          </Link>
          <div className="overline mt-2" style={{ color: "var(--rs-terracotta)" }}>
            Est. 2026
          </div>
        </div>

        {/* Primary nav — scrolls internally if the list ever gets too tall */}
        <nav
          data-testid="sidebar-nav"
          className="flex-1 overflow-y-auto px-6 pb-4 flex flex-col gap-3.5"
        >
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase transition-colors hover:text-bone ${
                  isActive ? "text-bone" : "text-muted-bone"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Admin lives BELOW a hairline divider so Journal can never crowd it. */}
        {user?.is_admin && (
          <div
            className="px-6 py-4 border-t"
            style={{ borderColor: "var(--rs-border)" }}
          >
            <Link
              data-testid="header-admin-link"
              to="/admin"
              className="text-[12px] uppercase tracking-[0.22em] hover:opacity-90 transition"
              style={{ color: "var(--rs-terracotta)" }}
            >
              ✦ Admin Suite
            </Link>
          </div>
        )}

        {/* Utility icons — anchored to the bottom of the rail */}
        <div
          className="px-6 py-5 border-t flex items-center gap-5"
          style={{ borderColor: "var(--rs-border)" }}
        >
          {utilityIcons}
        </div>
      </aside>
    </>
  );
};

export default Header;
